import { useContext, useEffect, useState } from 'react'
import { getBeatsApi, getVouchersApi, getReceiptSummaryDownloadApi, getUserListApi, getReceiptSummaryApi, approveBulkReceiptApi } from '../api/api.js'
import { showMessage } from '../context/MessageContext.jsx';
import { AuthContext } from '../context/AuthContext.jsx'
import { useOrganizationSettings } from '../context/OrganizationSettingsContext';
import { useCompany } from '../context/CompanyContext.jsx';
import ReactDatePicker from "react-datepicker";
import Select from "react-select";
import Pagination from '../components/ui/Pagination.jsx'
import { usePagination } from '../hooks/usePagination.js'
import './ReceiptList.css'

export default function VouchersList() {
  const [limit] = useState(30); // Items per page
  const [total, setTotal] = useState(0);
  const {user } = useContext(AuthContext);
  const { settings } = useOrganizationSettings();
  const { companyId } = useCompany();
  const pagination = usePagination(total, limit);

  const [beats, setBeats] = useState([]);
  const [userList, setUserList] = useState([]);
  const [selectedBeat, setSelectedBeat] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserLabel, setSelectedUserLabel] = useState(null);
  const [selectedBills, setSelectedBills] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectedReceiver, setSelectedReceiver] = useState('');

  const [rows, setRows] = useState([])
  const [amountData, setAmountData] = useState({})
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  // Fetch all beats on mount
  useEffect(() => {
    getBeatsApi()
      .then(res => setBeats(res.data?.data || res.data || []))
      .catch(() => setBeats([]));
  }, []);
  
  // Fetch all users on mount (only if userWise is enabled and user is owner)
  useEffect(() => {
    if (settings.userWise && user?.isOwner) {
      getUserListApi()
        .then(res => {
          const users = res.data?.data || res.data || [];
          setUserList(users);
          
          // Set default selection to logged-in user for owners
          const userId = user?.tallyUserId || user?.tallyUserId;
          if (userId && users.length > 0) {
            const defaultUser = users.find(sp => {
              const spId = sp.tallyUserId || sp.tallyUserId;
              return spId === userId;
            });
            if (defaultUser) {
              const spId = defaultUser.tallyUserId || defaultUser.tallyUserId;
              const spName = defaultUser.userName || defaultUser.userName;
              setSelectedUserId(spId);
              setSelectedUserLabel(`${spName} - ${spId}`);
            }
          }
        })
        .catch(() => setUserList([]));
    }
  }, [settings.userWise, user?.isOwner, user?.tallyUserId, user?.tallyUserId]);



  useEffect(() => {
  setLoading(true);
  const params = {
    ...(settings.beatWise && selectedBeat ? { beatName: selectedBeat } : {}),
    // For userWise: owners can select, non-owners auto-filter by logged-in user
    ...(settings.userWise ? {
      tallyUserId: user?.isOwner ? (selectedUserId || user?.tallyUserId || user?.tallyUserId) : (user?.tallyUserId || user?.tallyUserId)
    } : {}),
    // Company filtering (Case 3 & 4) - use global company from context
    ...(settings.userWise && companyId ? { companyId: companyId } : {}),
    ...(selectedReceiver ? { receiver: selectedReceiver } : {}),
    ...(startDate && endDate ? {
      startDate: startDate.toISOString().slice(0, 10),
      endDate: endDate.toISOString().slice(0, 10)
    } : {}),
    page: pagination.currentPage,
    limit,
  };
  getReceiptSummaryApi(params)
    .then((res) => {
      const data = res.data?.data || res.data || [];
      setRows(data.rows || data); // Adjust if your API returns { rows, total }
      setAmountData(res.data.amountData); // Adjust if your API returns { rows, total }
      setTotal(res.data.totalCount || 0);
    })
    .catch(() => {
      setRows([]);
      setTotal(0);
    })
    .finally(() => setLoading(false));
  }, [selectedBeat, selectedUserId, companyId, selectedReceiver, startDate, endDate, pagination.currentPage, limit, settings.userWise, user?.isOwner, user?.tallyUserId, user?.tallyUserId]);

  const handleBeatChange = e => {
    if (e === null) {
      setSelectedBeat(null);
    } else {
      setSelectedBeat(e.value);
    }
    pagination.resetPagination();
  };
  

  const handleUserChange = (selectedOption) => {
    // Only update if a selection was actually made (not when clicking outside)
    if (selectedOption === null) {
      // Only clear if explicitly cleared (X button), not on outside click
      setSelectedUserId(null);
      setSelectedUserLabel(null);
    } else if (selectedOption && selectedOption.value) {
      setSelectedUserId(selectedOption.value);
      setSelectedUserLabel(selectedOption.label);
    }
    pagination.resetPagination();    
  };

  const handleStartDateChange = (date) => {
    // Check if a date is selected
    if (date) {
      // Get the UTC time by stripping the local timezone offset
      const utcDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      setStartDate(utcDate);

    } else {
      setStartDate(new Date());
    }
  }
  
  const handleEndDateChange = (date) => {
    // Check if a date is selected
    if (date) {
      // Get the UTC time by stripping the local timezone offset
      const utcDate = new Date(
        date.getTime() - date.getTimezoneOffset() * 60000
      );
      setEndDate(utcDate);

    } else {
      setEndDate(new Date());
    }
  }

  const handleReceiverChange = e => {
    setSelectedReceiver(e.target.value);
  };


  const handleDownloadReport = async () => {
  if (!selectedBeat && !selectedUserId) {
    showMessage('Please select a Beat and User to download the report.', 'error');
    return;
  }
  try {
    setDownloading(true);
    const res = await getReceiptSummaryDownloadApi({ beatName: selectedBeat, tallyUserId: selectedUserId });
    const data = res.data;
    if (data.code === 200 && data.reportUrl) {
      setDownloading(false);
      window.open(data.reportUrl, '_blank');
    } else {
      setDownloading(false);
      showMessage(data.message || 'Failed to generate report.', 'error');
    }
  } catch (err) {
    setDownloading(false);
    console.error(err);
    showMessage('Error downloading report.', 'error');
    
  }
};

const handleApproveChange = async()=>{
  try {
    console.log(selectedBills);
    // let allocationId = e.target.value;
    const res = await approveBulkReceiptApi({ allocations: selectedBills });
    const data = res.data;
    if (data.code === 200) {
      showMessage(data.message, 'success');
      // Update isApproved in rows
      setRows(prevRows =>
        prevRows.map(row =>
          selectedBills.includes(row.id || row.receiptId)
          ? { ...row, isApproved: true }
          : row
        )
      );
      setSelectedBills([]); 
    } else {
      showMessage(data.message || 'Failed to update.', 'error');
    }
  } catch (err) {
    console.error(err);
    showMessage(data.message || 'Error in approve.', 'error');
  }
}

  const receiverOptions = [
    { value: 'Supply 1', label: 'Supply 1' },
    { value: 'Supply 2', label: 'Supply 2' },
    { value: 'Supply 3', label: 'Supply 3' },
    { value: 'Supply 4', label: 'Supply 4' },
    { value: 'Supply 5', label: 'Supply 5' },
  ];

  return (
    <div className="card">
      <h2>Collection Summary</h2>
      <div className="filter-container">
        <div className="d-flex flex-column flex-md-row gap-2" style={{ flex: 1 }}>
          
          {/* User */}
          {settings.userWise && user?.isOwner && (
            <div className="filter-item">
              <label className="form-label small fw-500 mb-2 d-block">User</label>
              <Select
                options={userList.map(b => {
                  const bId = b.tallyUserId || b.tallyUserId;
                  const bName = b.userName || b.userName;
                  return { value: bId, label: `${bName} - ${bId}` };
                })}
                value={
                  selectedUserId && selectedUserLabel
                    ? userList.find(u => u.tallyUserId === selectedUserId)
                      ? { value: selectedUserId, label: selectedUserLabel }
                      : null
                    : null
                }
                onChange={handleUserChange}
                onBlur={() => {}} // Prevent clearing on blur
                placeholder="Select User"
                isClearable={true}
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          )}

          {/* Beat */}
          {settings.beatWise && (
            <div className="filter-item">
              <label className="form-label small fw-500 mb-2 d-block">Beat</label>
              <Select
                options={beats.map(b => ({ value: b.beatName, label: b.beatName }))}
                value={selectedBeat ? { value: selectedBeat, label: selectedBeat } : null}
                onChange={handleBeatChange}
                placeholder="Select Beat"
                isClearable={true}
                isSearchable
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          )}

          {/* Start Date */}
          <div className="filter-item">
            <label className="form-label small fw-500 mb-2 d-block">Start Date</label>
            <ReactDatePicker
              selected={startDate}
              onChange={handleStartDateChange}
              className="form-control form-control-sm"
              dateFormat="dd-MM-yyyy"
              maxDate={new Date()}
              placeholderText="Start Date"
            />
          </div>

          {/* End Date */}
          <div className="filter-item">
            <label className="form-label small fw-500 mb-2 d-block">End Date</label>
            <ReactDatePicker
              selected={endDate}
              onChange={handleEndDateChange}
              className="form-control form-control-sm"
              dateFormat="dd-MM-yyyy"
              maxDate={new Date()}
              placeholderText="End Date"
            />
          </div>
          {/* Receiver */}
          <div className="filter-item">
            <label className="form-label small fw-500 mb-2 d-block">Receiver</label>
            <Select
              options={receiverOptions.map(b => ({ value: b.value, label: b.label }))}
              value={selectedReceiver ? { value: selectedReceiver, label: selectedReceiver } : null}
              onChange={(opt) => {
                handleReceiverChange({ target: { value: opt ? opt.value : "" } });
              }}
              placeholder="Select Receiver"
              isClearable
              isSearchable
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {/* Buttons Section */}
        <div className="filter-actions">
          {selectedBills.length > 0 && (
            <button className="btn btn-sm btn-primary flex-grow-1" onClick={handleApproveChange}>
              Approve
            </button>
          )}
        </div>
      </div>
      <div className="receipt-list-summary">
        <div className="receipt-list-badge receipt-list-badge-info">
          <span className="receipt-list-badge-label">Cash:</span>
          <span className="receipt-list-badge-value">₹{amountData.totalCash || 0}</span>
        </div>
        <div className="receipt-list-badge receipt-list-badge-primary">
          <span className="receipt-list-badge-label">UPI:</span>
          <span className="receipt-list-badge-value">₹{amountData.totalUpi || 0}</span>
        </div>
        <div className="receipt-list-badge receipt-list-badge-warning">
          <span className="receipt-list-badge-label">Cheque:</span>
          <span className="receipt-list-badge-value">₹{amountData.totalCheque || 0}</span>
        </div>
      </div>
      {loading ? 'Loading...' : (
        <div className="table-wrap">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>S.No</th>
                { settings.billWise && (
                  <>
                  <th>Bill Date</th>
                  <th>Bill Number</th>
                </>
                )}
                { !settings.billWise && (
                  <>
                    <th>Receipt Number</th>
                  </>
                )}
                <th>Customer</th>
                <th>Receiver</th>
                { settings.billWise && (
                  <>
                <th>Bill Amount</th>
                </>
                )}
                <th>Paid Amount</th>
                <th>Balance</th>
                { settings.billWise && (
                  <>
                    <th>Bill Due Date</th>
                  </>
                )}
                <th>Receipt Date</th>
                <th>Cash Amt</th>
                <th>UPI Amt</th>
                <th>Cheque Amt</th>
                <th>UPI File</th>
                <th>Sync</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="13" style={{ textAlign: 'center', padding: '2rem', color: '#546e7a' }}>
                    No records available
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => (
                  <tr key={r.id || idx}>
                    <td>{idx + 1}</td>
                    { settings.billWise && (
                      <>
                        <td>{(r.billDate || '').toString().slice(0, 10) ?? '-'}</td>
                        <td>{r.billNumber || '-'}</td>
                      </>
                    )}
                    { !settings.billWise && (
                      <>
                        <td>{r.receiptNumber || '-'}</td>
                      </>
                    )}
                    <td>{r?.customerName || '-'}</td>
                    <td>{r?.receiver || '-'}</td>
                    { settings.billWise && (
                      <>
                        <td>{r.billAmount ?? '-'}</td>
                      </>
                    )}
                    <td>{r.billPaidAmount ?? '-'}</td>
                    <td>{r.balance ?? '-'}</td>
                    { settings.billWise && (
                      <>
                        <td>{(r.billDueDate || '').toString().slice(0,10)}</td>
                      </>
                    )}
                    <td>{(r.receiptDate || '').toString().slice(0,10)}</td>
                    <td>{r.cashAmount ?? '0'}</td>
                    <td>{r.upiAmount ?? '0'}</td>
                    <td>{r.chequeAmount ?? '0'}</td>
                    <td>
                      {r.upiFileName ? (
                        <a href={r.upiFileName ?? '-'} download target='_blank' rel="noreferrer"><i className="bi bi-download" style={{ fontSize: '1.2em', color: '#198754' }}></i></a>
                      ) : ('-')}
                    </td>
                    <td>{r.isSynced ? <span className="badge bg-success">Yes</span> : <span className="badge bg-danger">No</span>}</td>
                    <td>
                      {r.isApproved != null ? (
                        <input type="checkbox" value={r.id || r.receiptId} checked={!!r.isApproved || selectedBills.includes(r.id || r.receiptId)} disabled={!!r.isApproved} 
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedBills(prev => [...prev, r.id || r.receiptId]);
                          } else {
                            setSelectedBills(prev => prev.filter(id => id !== r.id || r.receiptId));
                          }
                        }}
                      /> 
                      ) : ('-')}
                  </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {!loading && total > 0 && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={pagination.goToPage}
            />
          )}
        </div>
      )}
    </div>
  )
}
